import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * Sprint customization: when minus is clicked at quantity 1, dispatch
 * quantity=0 so the parent <cart-items-component> removes the line item.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   * Cart context: minus is ALWAYS enabled so clicking it at quantity 1
   * fires a quantity=0 event, which the parent <cart-items-component>
   * handles as a line item removal.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = false;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Overrides the parent updateQuantity to allow the value to drop to 0
   * instead of clamping at min. When the resulting value is 0, the
   * dispatched event tells cart-items-component to remove the line.
   *
   * @param {number} stepMultiplier - Positive to increase, negative to decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    let newValue = value + step * stepMultiplier;

    // Cart-specific: allow going below min all the way to 0 (= remove).
    if (newValue < min) {
      newValue = 0;
    }
    if (effectiveMax !== null && newValue > effectiveMax) {
      newValue = effectiveMax;
    }

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
